/**
 * PULSE Floating Buy Dock
 * ------------------------------------------------------------
 * There is only ever ONE Add to Bag button. It lives naturally in
 * .product-form-actions. Once its position marker scrolls out of
 * view, this script physically moves that same button into the
 * floating dock (#PulseFloatSlot), animating the move with a FLIP
 * transform so it visibly travels there instead of snapping.
 * Scroll back up and it flies back to its natural spot.
 *
 * No duplicate CTA exists, so its label, disabled state and
 * bundle CTA text are already correct at all times — owned
 * entirely by pulse-product-form.js / pulse-buy-options.js.
 *
 * The "+" toggle opens a panel that never re-implements bundle
 * selection: tapping a row here just clicks the matching real
 * .buy-option button up in #BuyOptions, so pulse-buy-options.js
 * stays the only place that decides what a bundle choice does.
 *
 * The dock is rendered at <body> level (see snippets/pulse-sticky-buy)
 * so no transformed ancestor can capture its fixed positioning —
 * that was the bug that used to trap a floating bar inside the FAQ.
 */
(function(){

  function flip(el, mutate){
    var first = el.getBoundingClientRect();
    mutate();
    var last = el.getBoundingClientRect();
    var dx = first.left - last.left;
    var dy = first.top - last.top;
    var sx = last.width ? (first.width / last.width) : 1;

    if(!dx && !dy && sx === 1) return;

    el.style.transition = 'none';
    el.style.transformOrigin = 'top left';
    el.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ')';

    /* force reflow so the browser commits the starting transform
       before we animate away from it */
    el.getBoundingClientRect();

    requestAnimationFrame(function(){
      el.style.transition = 'transform 0.34s var(--ease)';
      el.style.transform = '';
    });

    el.addEventListener('transitionend', function cleanup(ev){
      if(ev.propertyName !== 'transform') return;
      el.removeEventListener('transitionend', cleanup);
      el.style.transition = '';
      el.style.transformOrigin = '';
    });
  }

  function init(){
    var dock = document.getElementById('PulseFloatDock');
    if(!dock || dock.dataset.pulseInit === '1') return;

    var slot = document.getElementById('PulseFloatSlot');
    var marker = document.getElementById('ProductSubmitMarker');
    var mainBtn = document.getElementById('ProductSubmit');
    var toggle = document.getElementById('StickyBuyToggle');
    var panel = document.getElementById('StickyBuyOptions');

    /* No product form on this page — nothing to float */
    if(!slot || !marker || !mainBtn) return;

    dock.dataset.pulseInit = '1';

    /* Keep the button submitting the real form even once it's
       physically outside it in the DOM. */
    mainBtn.setAttribute('form', 'ProductForm');

    var homeParent = mainBtn.parentNode;
    var homeNext = mainBtn.nextSibling;
    var floated = false;

    function openPanel(){
      if(!panel || !toggle) return;
      panel.hidden = false;
      requestAnimationFrame(function(){ panel.classList.add('is-open'); });
      toggle.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }

    function closePanel(){
      if(!panel || !toggle) return;
      panel.classList.remove('is-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      window.setTimeout(function(){
        if(!panel.classList.contains('is-open')) panel.hidden = true;
      }, 260);
    }

    function float(on){
      if(on === floated) return;
      floated = on;

      dock.classList.toggle('is-visible', on);
      dock.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('has-sticky-buy', on);
      if(!on) closePanel();

      flip(mainBtn, function(){
        if(on){
          slot.appendChild(mainBtn);
        } else if(homeNext && homeNext.parentNode === homeParent){
          homeParent.insertBefore(mainBtn, homeNext);
        } else {
          homeParent.appendChild(mainBtn);
        }
      });
    }

    if(toggle && panel){
      toggle.addEventListener('click', function(){
        if(panel.classList.contains('is-open')){ closePanel(); }
        else { openPanel(); }
      });

      panel.addEventListener('click', function(ev){
        var row = ev.target.closest('.buy-option');
        if(!row) return;
        ev.preventDefault();

        var kind = row.getAttribute('data-option-kind');
        var realOptions = document.getElementById('BuyOptions');
        if(!realOptions) return;

        var target = kind === 'single'
          ? realOptions.querySelector('[data-option-kind="single"]')
          : realOptions.querySelector('[data-option-kind="bundle"][data-variant-id="' + row.getAttribute('data-variant-id') + '"]');

        if(target) target.click();

        panel.querySelectorAll('.buy-option').forEach(function(b){
          var on = (b === row);
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        closePanel();
      });
    }

    /* Float the moment the button's natural position scrolls out
       of view, so the move happens as early as it makes sense —
       not after an extra delay. */
    if('IntersectionObserver' in window){
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          float(scrolledPast);
        });
      }, { threshold: 0 });
      io.observe(marker);
    } else {
      window.addEventListener('scroll', function(){
        float(marker.getBoundingClientRect().bottom < 0);
      }, { passive: true });
    }
  }

  /* Leaving the product page.

     The shell router only swaps #MainContent, but this dock lives at
     <body> level and the real Add to Bag button has been physically
     moved INTO it. Without this, navigating to Science/Reviews left
     an orphaned "Add to Bag" floating over an unrelated page, wired
     to a product form that no longer exists in the document.

     Tear the dock down whenever the product form is gone, and clear
     the init flag so it rebuilds cleanly if the customer navigates
     back to the product. */
  function teardownIfOffProduct(){
    var dock = document.getElementById('PulseFloatDock');
    if(!dock) return;

    var stillOnProduct = !!document.querySelector('form[action*="/cart/add"] #ProductVariantId')
      || !!document.getElementById('ProductSubmitMarker');
    if(stillOnProduct) return;

    var orphan = document.getElementById('ProductSubmit');
    if(orphan && dock.contains(orphan)) orphan.parentNode.removeChild(orphan);

    dock.classList.remove('is-visible');
    dock.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-sticky-buy');
    dock.dataset.pulseInit = '';
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('pulse:navigated', function(){
    teardownIfOffProduct();
    init();
  });
})();
